<template>
    <Portal to="sidepanel">
        <SidePanel
            :stepped-tabs="true"
            :isTeachSidePanel="true"
            name="Add To Group"
            class="add-to-group"
            @close="emitClose"
        >
            <label>Select Group</label>
            <p class="add-to-group__text">Which group should these students be added to?</p>
            <PocketRadio
                v-model="selectGroupType"
                class="add-to-group__toggle-groups"
                :data="[
                    { label: 'Add to Existing Group', value: 'existingGroup' },
                    { label: 'Create New Group', value: 'newGroup' }
                ]"
            />
            <div
                class="add-to-group__existing-groups"
            >
                <PocketSelect
                    v-if="selectGroupType && selectGroupType.value === 'existingGroup'"
                    v-model="selectedGroup"
                    label="Group Name"
                    class="add-to-group__field"
                    :data="groupOptions"
                    :show-empty-option="false"
                    placeholder="Select Group"
                >
                    <template #dropdownListItem="{ item }">
                        <div class="add-to-group__group-name">
                            {{ item.label }}
                        </div>
                        <div class="add-to-group__group-instructor" v-if="'instructor' in item">
                            {{ item.instructor }}
                        </div>
                    </template>
                </PocketSelect>
            </div>
            <div
                class="add-to-group__new-group"
                v-if="selectGroupType && selectGroupType.value === 'newGroup'"
            >
                <PocketInput
                    v-model="newGroupName"
                    label="Group Name"
                    class="add-to-group__field"
                />
                <PocketSelect
                    v-model="examPrep"
                    :disabled="isExamDropdownDisabled"
                    label="Exam Prep"
                    class="add-to-group__field"
                    :data="orgExamsForStudents"
                    :placeholder="isExamDropdownDisabled
                        ? orgExamsForStudents[0]?.label
                        : 'Select exam prep'"
                />
                <PocketRadio
                    v-model="usePermission"
                    label="Who can <strong>use</strong> it?"
                    class="add-to-group__field add-to-group__radio-field add-to-group__use-permission"
                    :data="[
                        { label: 'Colleagues in organization', value: 1 },
                        { label: 'Just me', value: 2 }
                    ]"
                />
                <PocketRadio
                    v-model="editPermission"
                    label="Who can <strong>edit</strong> this group?"
                    class="add-to-group__field add-to-group__radio-field add-to-group__edit-permission"
                    :disabled="!usePermission || usePermission.value !== 1"
                    :data="[
                        { label: 'Colleagues in organization', value: 1 },
                        { label: 'Just me', value: 2 }
                    ]"
                />
            </div>
            <template #action>
                <PocketButton
                    type="primary"
                    :disabled="isAddStudentsBtnDisabled"
                    @click="submitAddStudents"
                >
                    Add {{ studentIds.length }} Students
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { examModule } from '@/store/exam/module'
import { organizationModule } from '@/store/organization/module'
import { licenseModule } from '@/store/license/module'
import { instructorModule } from '@/store/instructor/module'
import { studentModule } from '@/store/student/module'
import { groupModule } from '@/store/group/module'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'

type TGroupOption = {
    value: string
    label: string
    libraryKey?: string
    instructor?: string
}

const {
    SidePanel,
    Button: PocketButton,
    Radio: PocketRadio,
    Select: PocketSelect,
    Input: PocketInput,
} = UIKit

const props = defineProps<{
    orgStudentIds: string[]
    orgExamIds: string[]
}>()

const emit = defineEmits<{
    close: [value?: boolean]
}>()

const newGroupName = ref('')
const examPrep = ref<null | { value: string; label: string }>(null)
const usePermission = ref<null | { value: 1 | 2; label: string }>(null)
const editPermission = ref<null | { value: 1 | 2; label: string }>(null)
const selectedGroup = ref<null | { value: string; label: string }>(null)
const selectGroupType = ref<null | TGroupOption>(null)
const groupTypeOptions = [
    { label: 'Add to Existing Group', value: 'existingGroup' },
    { label: 'Create New Group', value: 'newGroup' },
]
const studentIds = ref<string[]>([])
const examIds = ref<string[]>([])
const isLoading = ref(false)

const examMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const currentOrgGroups = computed(() => {
    return groupModule.getters.getCurrentOrgGroups()
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const currentOrgInstructors = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructors()
})

const currentOrgInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const isAddStudentsBtnDisabled = computed(() => {
    const isDisabledForNewGroup = !(newGroupName.value && examPrep.value && usePermission.value)
    const isDisabledForExistingGroup = !(selectedGroup.value?.value && examPrep.value)
    return (selectGroupType.value?.value === 'existingGroup' && isDisabledForExistingGroup)
        || (selectGroupType.value?.value === 'newGroup' && isDisabledForNewGroup)
})

const isExamDropdownDisabled = computed(() => {
    return studentIds.value.length > 1 || props.orgExamIds.length === 1
})

const orgExamsForStudents = computed(() => {
    return examIds.value.map(ei => ({
        value: currentOrgExams.value[ei]?.objectId || '',
        label: currentOrgExams.value[ei]?.displayName || '',
    }))
})

const groupOptions = computed(() => {
    return Object.values(currentOrgGroups.value).reduce((acc, orgGroup) => {
        const instructorId = orgGroup.owner?.objectId
        if (
            props.orgExamIds.includes(orgGroup.orgExam.objectId)
            && (
                orgGroup.type === 'public'
                || (orgGroup.type === 'private' && instructorId === currentOrgInstructor.value?.objectId)
            )
        ) {
            const name = groupModule.getters.getGroupName(orgGroup.objectId)
            const examMetadataId = currentOrgExams.value[orgGroup.orgExam.objectId]?.examMetadata.objectId
            const examGuid = examMetadataId ? examMetadata.value[examMetadataId]?.examGuid : undefined
            if (!name || !examGuid) {
                return acc
            }
            const libraryKey = groupModule.getters.getGroupCompositeKeyForGroupId(orgGroup.objectId)
            if (acc.find(groupOption => groupOption.libraryKey === libraryKey)) {
                return acc
            }
            if (!instructorId) {
                return acc
            }
            const instructor = currentOrgInstructors.value?.[instructorId]
            if (!instructor) {
                return acc
            }
            const instructorUser = currentOrgInstructorUsers.value?.[instructor.user.objectId]

            const canAddSomeStudentToGroup = props.orgStudentIds.some(orgStudentId => {
                const isStudentAlreadyInGroup = groupModule.getters.getIsStudentInGroup({
                    studentId: orgStudentId,
                    groupId: orgGroup.objectId,
                })
                return !isStudentAlreadyInGroup
            })
            if (!canAddSomeStudentToGroup) {
                return acc
            }

            acc.push({
                value: orgGroup.objectId,
                label: orgGroup.name,
                libraryKey,
                instructor: `${instructorUser?.firstName} ${instructorUser?.lastName}`,
            })
        }
        return acc
    }, [] as TGroupOption[])
})

const emitClose = () => {
    emit('close', true)
}

const submitAddStudents = async () => {
    if (!currentOrgInstructor.value) {
        throw new Error('submitAddStudents: Unable to find current org instructor')
    }
    if (!currentOrganization.value) {
        throw new Error('submitAddStudents: Unable to find current organization')
    }
    if (!examPrep.value) {
        throw new Error('submitAddStudents: No exam prep has been selected')
    }

    isLoading.value = true

    if (selectedGroup.value?.value) {
        const orgGroupId = selectedGroup.value.value

        await studentModule.actions.addStudentsToGroup({
            orgGroupId,
            orgStudentIds: studentIds.value,
        })

    } else if (newGroupName.value) {
        await groupModule.actions.addGroup({
            ownerId: currentOrgInstructor.value.objectId,
            organizationId: currentOrganization.value.objectId,
            orgStudentIds: studentIds.value,
            orgExamId: examPrep.value.value,
            name: newGroupName.value,
            type: usePermission.value?.value === 1 ? 'public' : 'private',
            isPubliclyEditable: editPermission.value?.value === 1 ? true : false,
        })
    }
    organizationModule.actions.validateOrgState()
    isLoading.value = false
    emitClose()
}

watch(usePermission, (newVal) => {
    if (newVal?.value === 2) {
        editPermission.value = { value: 2, label: '' }
    }
}, { deep: true })

onMounted(async () => {
    await Promise.all([
        examModule.actions.fetchCurrentOrgExams(),
        groupModule.actions.fetchCurrentOrgGroups(),
        studentModule.actions.fetchCurrentOrgStudents(),
        studentModule.actions.fetchCurrentOrgStudentUsers(),
        instructorModule.actions.fetchCurrentOrgInstructor(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
        examModule.actions.fetchYourOrgExamIds(),
    ])

    studentIds.value = props.orgStudentIds
    examIds.value = props.orgExamIds
    examPrep.value = orgExamsForStudents.value[0] || null
    selectGroupType.value = groupTypeOptions[0] || null

    await userModule.actions.trackEvent('New_Group_Started')
})
</script>

<style lang="scss" scoped>
.add-to-group {
    &__text {
        margin-bottom: 25px;
    }

    &__toggle-groups {
        margin-left: 22px;
    }

    &__field {
        margin-bottom: 21px;
        max-width: 363px;
        margin-left: -11px;
    }

    &__radio-field {
        margin-left: 0;
    }

    &__back-arrow {
        margin-right: 10px;
        transform: rotate(180deg);
    }

    &__add-student {
        padding: 12px 11px;
        font-weight: 600;
        margin-left: -11px;
    }

    &__add-to-group-arrow,
    &__preview-group-arrow {
        margin-left: 6px;
    }

    &__students-table {
        margin-left: -11px;
    }

    &__checkbox-name {
        display: flex;
        align-items: center;
    }

    &__checkbox {
        margin-right: 10px;
    }

    &__name {
        width: calc(100% - 28px);
    }

    &__existing-groups {
        margin: 25px 10px 0;
    }

    &__new-group {
        margin: 25px 10px 0;
    }

    &__group-name {
        font-size: 15px;
        white-space: nowrap;
    }

    &__group-instructor {
        font-size: 13px;
        line-height: 18px;
        color: $slate-03;
    }
}
</style>
